import DateFnsUtils from '@date-io/date-fns';
import {
    Checkbox as MuiCheckbox,
    CheckboxProps,
    FormControl,
    FormHelperText,
    InputLabel,
    makeStyles,
    MenuItem,
    Radio as MuiRadio,
    RadioProps,
    Select as MuiSelect,
    SelectProps,
    TextField as MuiTextField,
    TextFieldProps,
} from '@material-ui/core';
import {
    DatePicker as MuiDatePicker,
    DatePickerProps,
    DateTimePicker as MuiDateTimePicker,
    DateTimePickerProps,
    MuiPickersUtilsProvider,
    TimePicker as MuiTimePicker,
    TimePickerProps,
} from '@material-ui/pickers';
import clsx from 'clsx';
import React from 'react';
import { Calendar, Clock } from 'react-feather';
import { usePrefixTranslation } from '../../i18n';
import { theme } from '../../theme/theme';

const useCheckboxRadioClasses = makeStyles({
    root: {
        '&.MuiCheckbox-root, &.MuiRadio-root': {
            color: theme.colors.neutral[2],
            '&:hover': {
                background: 'rgba(0, 0, 0, 0.05)',
            },
        },
    },
});

/**
 * Themed material-ui checkbox, some properties have their types enforced to only a subset of the previous type.
 *
 * @param props.color enforced type
 */
export const Checkbox = (props: CheckboxProps & { color?: never }) => {
    const classes = useCheckboxRadioClasses();

    return <MuiCheckbox {...props} className={clsx(classes.root, props.className)} />;
};

/**
 * Themed material-ui radio, some properties have their types enforced to only a subset of the previous type.
 *
 * @param props.color enforced type
 */
export const Radio = (props: RadioProps & { color?: never }) => {
    const classes = useCheckboxRadioClasses();

    return <MuiRadio {...props} className={clsx(classes.root, props.className)} />;
};

const useSelectTextFieldClasses = makeStyles({
    root: {
        '&.MuiTextField-root': {
            color: theme.colors.neutral[1],
            ...theme.typography.content.text,
        },
        '& .MuiFormLabel-root': {
            color: theme.colors.neutral[2],
            textTransform: 'capitalize',
            '&.Mui-focused, &.MuiFormLabel-filled': {
                color: 'currentColor',
            },
            '&.Mui-error': {
                color: theme.colors.red[1],
            },
            '&.Mui-disabled': {
                color: theme.colors.neutral[3],
            },
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: theme.colors.neutral[2],
            },
            '&.Mui-focused fieldset, &:hover fieldset': {
                borderColor: theme.colors.green[4],
            },
            '&.Mui-error fieldset': {
                borderColor: theme.colors.red[1],
            },
            '&.Mui-disabled fieldset': {
                borderColor: theme.colors.neutral[3],
            },
        },
        '& .MuiSelect-icon': {
            color: theme.colors.neutral[1],
        },
    },
});

/**
 * Themed material-ui text field, some properties have their types enforced to only a subset of the previous type.
 *
 * @param props.color enforced type
 * @param props.variant enforced type
 */
export const TextField = (props: TextFieldProps & { color?: never; variant?: never }) => {
    const classes = useSelectTextFieldClasses();
    const labelId = Math.random().toString();

    return <MuiTextField {...props} id={labelId} className={clsx(classes.root, props.className)} variant='outlined' />;
};

/**
 * Themed material-ui select, some properties have their types enforced to only a subset of the previous type.
 * This component also implements extra functionality for creating select items.
 *
 * @param props.color enforced type
 * @param props.variant enforced type
 * @param props.onChange enforced type
 * @param props.defaultValue enforced type
 * @param props.value enforced type
 * @param props.options list of select options
 * @param props.optionEquals options comparator, defaults to strict comparison
 * @param props.renderOption render the contents of an option
 * @param props.unselect unselect option element, the change value is `undefined`, it is only displayed if `props.value`
 *        or `props.defaultValue` is not present in `props.options`
 * @param props.onValue enforced type
 */
export const Select = <T extends any>(
    props: SelectProps & {
        color?: never;
        variant?: never;
        onChange?: never;
        defaultValue?: T;
        value?: T;
        options?: readonly T[];
        optionEquals?: (a?: T, b?: T) => boolean;
        renderOption?: (option: T) => React.ReactNode;
        helperText?: string;
        unselect?: React.ReactNode;
        onValue?: (option?: T) => void;
    },
) => {
    const {
        options = [],
        renderOption = String,
        optionEquals = (a?: T, b?: T) => a === b,
        helperText,
        unselect,
        onValue,
        ...selectProps
    } = props;
    const classes = useSelectTextFieldClasses();
    const defaultValueIndex = options.findIndex(o => optionEquals(o, props.defaultValue));
    const valueIndex = options.findIndex(o => optionEquals(o, props.value));
    const labelId = Math.random().toString();

    return (
        <FormControl className={clsx(classes.root, props.className)} variant='outlined' error={props.error}>
            {props.label && <InputLabel id={labelId}>{props.label}</InputLabel>}
            <MuiSelect
                {...selectProps}
                SelectDisplayProps={{ 'aria-labelledby': labelId }}
                MenuProps={{ disableScrollLock: true }}
                defaultValue={'defaultValue' in props ? defaultValueIndex : undefined}
                value={'value' in props ? valueIndex : undefined}
                onChange={event => onValue?.(options[event.target.value as number])}
            >
                {unselect && (defaultValueIndex === -1 || valueIndex === -1) && (
                    <MenuItem value={-1}>{unselect}</MenuItem>
                )}
                {options.map((option, i) => (
                    <MenuItem key={i} value={i}>
                        {renderOption(option)}
                    </MenuItem>
                ))}
            </MuiSelect>
            {helperText != undefined && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
};

const useDateTimeDialogClasses = makeStyles({
    root: {
        '& .MuiDialogContent-root': {
            color: theme.colors.neutral[1],
            '& .MuiToolbar-root': {
                background: theme.colors.neutral[1],
                color: theme.colors.white,
            },
            // date-year picker classes
            '& .MuiPickersYear-root, & .MuiPickersYear-yearSelected': {
                color: theme.colors.neutral[1],
            },
            // date-day picker classes
            '& .MuiPickersDay-day': {
                color: theme.colors.neutral[1],
            },
            '& .MuiPickersDay-daySelected': {
                background: theme.colors.neutral[1],
                color: theme.colors.white,
            },
            // time picker classes
            '& .MuiPickersClock-pin, & .MuiPickersClockPointer-pointer': {
                background: theme.colors.neutral[1],
            },
            '& .MuiPickersClockPointer-thumb': {
                background: theme.colors.white,
                borderColor: theme.colors.neutral[1],
            },
            '& .MuiPickersClockNumber-clockNumber': {
                color: theme.colors.neutral[2],
            },
            '& .MuiPickersClockNumber-clockNumberSelected': {
                background: theme.colors.neutral[1],
                color: theme.colors.white,
            },
            // date time picker classes
            '& .MuiTabs-root': {
                background: theme.colors.neutral[1],
                '& .MuiTabs-indicator': {
                    background: theme.colors.neutral[2],
                },
            },
        },
        '& .MuiDialogActions-root': {
            '& .MuiButton-text': {
                color: theme.colors.neutral[1],
                '&:hover': {
                    background: 'rgba(0, 0, 0, 0.05)',
                },
            },
        },
    },
});

/**
 * Themed material-ui date picker, some properties have their types enforced to only a subset of the previous type.
 *
 * @param props.color enforced type
 * @param props.inputVariant enforced type
 */
export const DatePicker = (props: DatePickerProps & { color?: never; inputVariant?: never }) => {
    const inputClasses = useSelectTextFieldClasses();
    const dialogClasses = useDateTimeDialogClasses();
    const { t } = usePrefixTranslation('global', { global: 'Form' });

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <MuiDatePicker
                {...props}
                className={clsx(inputClasses.root, props.className)}
                InputProps={{ endAdornment: <Calendar strokeWidth={1.5} /> }}
                labelFunc={date => t('date-picker', { date })}
                inputVariant='outlined'
                DialogProps={{
                    ...props.DialogProps,
                    className: clsx(dialogClasses.root, props.DialogProps?.className),
                    disableScrollLock: true,
                }}
            />
        </MuiPickersUtilsProvider>
    );
};

/**
 * Themed material-ui time picker, some properties have their types enforced to only a subset of the previous type.
 *
 * @param props.color enforced type
 * @param props.inputVariant enforced type
 */
export const TimePicker = (props: TimePickerProps & { color?: never; inputVariant?: never }) => {
    const inputClasses = useSelectTextFieldClasses();
    const dialogClasses = useDateTimeDialogClasses();

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <MuiTimePicker
                {...props}
                className={clsx(inputClasses.root, props.className)}
                InputProps={{ endAdornment: <Clock strokeWidth={1.5} /> }}
                inputVariant='outlined'
                DialogProps={{
                    ...props.DialogProps,
                    className: clsx(dialogClasses.root, props.DialogProps?.className),
                    disableScrollLock: true,
                }}
            />
        </MuiPickersUtilsProvider>
    );
};

/**
 * Themed material-ui date-time picker, some properties have their types enforced to only a subset of the previous type.
 *
 * @param props.color enforced type
 * @param props.inputVariant enforced type
 */
export const DateTimePicker = (props: DateTimePickerProps & { color?: never; inputVariant?: never }) => {
    const inputClasses = useSelectTextFieldClasses();
    const dialogClasses = useDateTimeDialogClasses();
    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <MuiDateTimePicker
                {...props}
                className={clsx(inputClasses.root, props.className)}
                InputProps={{
                    endAdornment: (
                        <>
                            <Calendar strokeWidth={1.5} />
                            <Clock strokeWidth={1.5} />
                        </>
                    ),
                }}
                inputVariant='outlined'
                DialogProps={{
                    ...props.DialogProps,
                    className: clsx(dialogClasses.root, props.DialogProps?.className),
                    disableScrollLock: true,
                }}
            />
        </MuiPickersUtilsProvider>
    );
};
